/**
 * Prevailing-wind rose for generation-layout orientation.
 *
 * Fetches one full calendar year of hourly 10 m wind direction/speed from the
 * Open-Meteo ERA5 archive (no API key) and bins it into 16 compass sectors.
 * The result is snapshotted into `GenerationLayoutConfiguration.windRose` —
 * like the FEMA exclusions, the network fetch happens once at click time and
 * the stored rose is what the UI/exhibit render, so published layouts stay
 * reproducible.
 *
 * The prevailing sector is frequency-weighted (hours from that direction),
 * with mean speed reported per sector for context. Calm hours (< 0.5 m/s)
 * are excluded from sector shares and reported separately.
 */
import type { GenerationLayoutWindRose } from '../../types/generationImpact';
import { throwIfAborted } from '../../lib/abortSignal';

const SECTOR_COUNT = 16;
const SECTOR_WIDTH_DEG = 360 / SECTOR_COUNT;
const CALM_THRESHOLD_MPS = 0.5;
const OPEN_METEO_ARCHIVE = 'https://archive-api.open-meteo.com/v1/era5';

export type WindRoseFetchResult =
  | { status: 'available'; windRose: GenerationLayoutWindRose }
  | { status: 'unavailable'; reason: string };

export interface FetchWindRoseOptions {
  signal?: AbortSignal;
  /** Test seam: replaces the network call. */
  fetchImpl?: typeof fetch;
  /** Test seam: replaces the wall clock. */
  now?: () => Date;
}

interface OpenMeteoArchiveResponse {
  hourly?: {
    wind_direction_10m?: Array<number | null>;
    wind_speed_10m?: Array<number | null>;
  };
  error?: boolean;
  reason?: string;
}

function round(value: number, digits: number): number {
  const scale = 10 ** digits;
  return Math.round(value * scale) / scale;
}

/** Bin hourly FROM-direction/speed pairs into the 16-sector rose. */
export function binWindRose(
  directionsDeg: ReadonlyArray<number | null>,
  speedsMps: ReadonlyArray<number | null>,
  meta: { source: string; retrievedAt: string; periodStart: string; periodEnd: string },
): GenerationLayoutWindRose | null {
  const totals = Array.from({ length: SECTOR_COUNT }, () => ({ hours: 0, speedSum: 0 }));
  let calmHours = 0;
  let validHours = 0;
  for (let index = 0; index < directionsDeg.length; index += 1) {
    const direction = directionsDeg[index];
    const speed = speedsMps[index];
    if (direction == null || speed == null
      || !Number.isFinite(direction) || !Number.isFinite(speed)) continue;
    validHours += 1;
    if (speed < CALM_THRESHOLD_MPS) {
      calmHours += 1;
      continue;
    }
    const sector = Math.round(((direction % 360) + 360) % 360 / SECTOR_WIDTH_DEG) % SECTOR_COUNT;
    totals[sector]!.hours += 1;
    totals[sector]!.speedSum += speed;
  }
  if (validHours === 0) return null;

  const directionalHours = validHours - calmHours;
  const sectors = totals.map((total, index) => ({
    fromDeg: index * SECTOR_WIDTH_DEG,
    frequencyPct: directionalHours > 0
      ? round(total.hours / directionalHours * 100, 2)
      : 0,
    meanSpeedMps: total.hours > 0 ? round(total.speedSum / total.hours, 2) : 0,
  }));
  const prevailing = sectors.reduce((best, sector) => (
    sector.frequencyPct > best.frequencyPct ? sector : best
  ), sectors[0]!);

  return {
    sectors,
    prevailingFromDeg: prevailing.fromDeg,
    calmPct: round(calmHours / validHours * 100, 2),
    ...meta,
  };
}

/**
 * Fetch the previous full calendar year of hourly winds at a point. Uses the
 * previous year so the window is stable within a calendar year and the ERA5
 * archive is guaranteed complete.
 */
export async function fetchWindRose(
  point: { lat: number; lng: number },
  options: FetchWindRoseOptions = {},
): Promise<WindRoseFetchResult> {
  const now = options.now?.() ?? new Date();
  const retrievedAt = now.toISOString();
  if (!Number.isFinite(point.lat) || !Number.isFinite(point.lng)) {
    return { status: 'unavailable', reason: 'layout anchor is not a valid coordinate' };
  }
  const year = now.getUTCFullYear() - 1;
  const periodStart = `${year}-01-01`;
  const periodEnd = `${year}-12-31`;
  const url = `${OPEN_METEO_ARCHIVE}?latitude=${point.lat.toFixed(5)}&longitude=${point.lng.toFixed(5)}`
    + `&start_date=${periodStart}&end_date=${periodEnd}`
    + '&hourly=wind_direction_10m,wind_speed_10m&wind_speed_unit=ms&timezone=UTC';

  throwIfAborted(options.signal);
  const fetchImpl = options.fetchImpl ?? fetch;
  let payload: OpenMeteoArchiveResponse;
  try {
    const response = await fetchImpl(url, { signal: options.signal ?? null });
    if (!response.ok) {
      return { status: 'unavailable', reason: `Open-Meteo archive returned HTTP ${response.status}` };
    }
    payload = await response.json() as OpenMeteoArchiveResponse;
  } catch (error) {
    if (options.signal?.aborted) throw error;
    return { status: 'unavailable', reason: 'Open-Meteo archive request failed' };
  }
  throwIfAborted(options.signal);
  if (payload.error) {
    return { status: 'unavailable', reason: payload.reason ?? 'Open-Meteo archive rejected the query' };
  }

  const windRose = binWindRose(
    payload.hourly?.wind_direction_10m ?? [],
    payload.hourly?.wind_speed_10m ?? [],
    {
      source: `open-meteo-era5 hourly 10m winds ${periodStart}..${periodEnd}`,
      retrievedAt,
      periodStart,
      periodEnd,
    },
  );
  if (!windRose) {
    return { status: 'unavailable', reason: 'Open-Meteo archive returned no usable wind hours' };
  }
  return { status: 'available', windRose };
}
